import { FC, FormEvent, ReactNode, useEffect, useState } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { dashboardsService } from '@/services/dashboards.service';
import type { MetaMaquina, MetasResposta } from '@/types/dashboards';
import { errosDaMeta, hojeIso, unidadesDoTexto, type ErrosMeta } from '@/utils/equiplexMetas';
import { fmtData, fmtDataHora, fmtUn } from '@/utils/painelFormat';

interface Props {
  /** Depois de salvar: a aba busca o payload de novo (a meta vale na hora, sem nova carga). */
  onSalva?: () => void;
}

const CAMPO =
  'w-full rounded-lg border border-theme-border bg-surface-primary px-3 py-2 text-sm text-content-primary focus:border-accent focus:outline-none';
const SEM_ERROS: ErrosMeta = { geral: '', campos: {} };

const Campo: FC<{ id: string; rotulo: string; erro?: string; dica?: string; children: ReactNode }> = ({
  id,
  rotulo,
  erro,
  dica,
  children,
}) => (
  <div className="flex flex-col gap-1">
    <label htmlFor={id} className="text-xs font-medium text-content-secondary">
      {rotulo}
    </label>
    {children}
    {erro ? (
      <p id={`${id}-erro`} className="text-xs text-theme-danger">
        {erro}
      </p>
    ) : (
      dica && <p className="text-xs text-content-muted">{dica}</p>
    )}
  </div>
);

const quem = (m: MetaMaquina) => m.autor?.nome ?? 'carga inicial';

type MaquinaMeta = MetasResposta['maquinas'][number];
const nome = (m: Pick<MaquinaMeta, 'maquina' | 'rotulo'>) => m.rotulo ?? m.maquina;

const TabelaMetas: FC<{ resposta: MetasResposta }> = ({ resposta }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[520px] text-sm" data-testid="metas-tabela">
      <thead>
        <tr className="border-b border-theme-border text-left text-2xs uppercase tracking-wide text-content-muted">
          <th className="py-2 pr-3 font-medium">Máquina</th>
          <th className="py-2 pr-3 text-right font-medium">Meta hoje (un/dia)</th>
          <th className="py-2 pr-3 font-medium">Desde</th>
          <th className="py-2 font-medium">Próximo ajuste</th>
        </tr>
      </thead>
      <tbody className="tabular-nums">
        {resposta.maquinas.map((m) => (
          <tr key={m.maquina} className="border-b border-theme-border" data-testid={`meta-${m.maquina}`}>
            <td className="py-2 pr-3">
              <div className="text-content-primary">{nome(m)}</div>
              <div className="text-xs text-content-muted">{m.linha}</div>
            </td>
            <td className="py-2 pr-3 text-right font-semibold text-content-primary">{m.vigente ? fmtUn(m.vigente.unidades_dia) : '—'}</td>
            <td className="py-2 pr-3 text-content-secondary">{m.vigente ? `${fmtData(m.vigente.vigente_desde)} · ${quem(m.vigente)}` : 'sem meta: vale a OP'}</td>
            <td className="py-2 text-content-secondary">
              {m.agendada ? `${fmtUn(m.agendada.unidades_dia)} a partir de ${fmtData(m.agendada.vigente_desde)}` : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Historico: FC<{ itens: MetaMaquina[]; nomes: Map<string, string> }> = ({ itens, nomes }) => (
  <details className="text-sm">
    <summary className="cursor-pointer text-content-secondary">Histórico de ajustes ({itens.length})</summary>
    <ul className="mt-2 space-y-1 text-xs text-content-secondary" data-testid="metas-historico">
      {itens.map((m) => (
        <li key={m.id}>
          <span className="text-content-primary">{nomes.get(m.maquina) ?? m.maquina}</span>: {fmtUn(m.unidades_dia)}/dia a partir de{' '}
          {fmtData(m.vigente_desde)} —{' '}
          {quem(m)}, {fmtDataHora(m.created_at)}
          {m.observacao && <> · {m.observacao}</>}
        </li>
      ))}
    </ul>
  </details>
);

interface FormProps {
  resposta: MetasResposta;
  onSalvar: (dados: { maquina: string; unidades: number; desde: string; observacao: string }) => Promise<ErrosMeta | null>;
}

const FormMeta: FC<FormProps> = ({ resposta, onSalvar }) => {
  const [maquina, setMaquina] = useState(resposta.maquinas[0]?.maquina ?? '');
  const [valor, setValor] = useState('');
  const [desde, setDesde] = useState(hojeIso());
  const [observacao, setObservacao] = useState('');
  const [erros, setErros] = useState<ErrosMeta>(SEM_ERROS);
  const [salvando, setSalvando] = useState(false);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    const unidades = unidadesDoTexto(valor);
    if (unidades === null) {
      setErros({ geral: '', campos: { unidades_dia: 'Digite a meta em unidades por dia (ex.: 45000; 0 = sem produção).' } });
      return;
    }
    setSalvando(true);
    const falha = await onSalvar({ maquina, unidades, desde, observacao });
    setSalvando(false);
    setErros(falha ?? SEM_ERROS);
    if (!falha) {
      setValor('');
      setObservacao('');
    }
  };

  return (
    <form onSubmit={enviar} className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_2fr_auto] xl:items-start" aria-label="Ajustar meta">
      <Campo id="meta-maquina" rotulo="Máquina" erro={erros.campos.maquina}>
        <select id="meta-maquina" className={CAMPO} value={maquina} onChange={(e) => setMaquina(e.target.value)}>
          {resposta.maquinas.map((m) => (
            <option key={m.maquina} value={m.maquina}>
              {nome(m)} ({m.linha})
            </option>
          ))}
        </select>
      </Campo>
      <Campo id="meta-valor" rotulo="Nova meta (un/dia)" erro={erros.campos.unidades_dia}>
        <input id="meta-valor" className={CAMPO} inputMode="numeric" value={valor} onChange={(e) => setValor(e.target.value)} />
      </Campo>
      <Campo
        id="meta-desde"
        rotulo="Vale a partir de"
        erro={erros.campos.vigente_desde}
        dica="Os dias antes ficam com a meta anterior. Para um dia parado, meta 0 nele e a meta de volta no dia seguinte."
      >
        <input id="meta-desde" type="date" className={CAMPO} value={desde} required onChange={(e) => setDesde(e.target.value)} />
      </Campo>
      <Campo id="meta-obs" rotulo="Motivo (opcional)" erro={erros.campos.observacao}>
        <input id="meta-obs" className={CAMPO} maxLength={500} value={observacao} onChange={(e) => setObservacao(e.target.value)} />
      </Campo>
      <div className="flex flex-col gap-1 xl:pt-5">
        <button
          type="submit"
          disabled={salvando}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
        >
          {salvando ? 'Salvando…' : 'Salvar meta'}
        </button>
      </div>
      {erros.geral && <p className="text-xs text-theme-danger sm:col-span-2 xl:col-span-5">{erros.geral}</p>}
    </form>
  );
};

/**
 * Meta diária das máquinas da daily: a programação do sopro (sopradoras 6 a 9) e do envase (GF-1,
 * GF-2, Rommelag 1 e 2), decisões do PCP de 15 e 16/10/2026. Ajustar não apaga nada: grava uma meta
 * nova a partir de um dia, com quem fez.
 */
const MetasMaquinas: FC<Props> = ({ onSalva }) => {
  const { showToast } = useToast();
  const [resposta, setResposta] = useState<MetasResposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    dashboardsService
      .listarMetas()
      .then((r) => vigente && setResposta(r))
      .catch(() => vigente && setErro('Não foi possível carregar as metas.'));
    return () => {
      vigente = false;
    };
  }, []);

  const salvar: FormProps['onSalvar'] = async ({ maquina, unidades, desde, observacao }) => {
    try {
      setResposta(await dashboardsService.salvarMeta({ maquina, unidades_dia: unidades, vigente_desde: desde, observacao }));
      const rotulo = resposta?.maquinas.find((m) => m.maquina === maquina);
      showToast(`Meta da ${rotulo ? nome(rotulo) : maquina}: ${fmtUn(unidades)}/dia a partir de ${fmtData(desde)}.`, 'success');
      onSalva?.();
      return null;
    } catch (e) {
      return errosDaMeta(e);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-theme-border bg-surface-primary p-4" data-testid="metas-maquinas">
      <div>
        <h3 className="text-sm font-semibold text-content-primary">Meta das máquinas</h3>
        <p className="text-xs text-content-muted">
          A programação da daily é a meta de cada máquina por dia, todos os dias do período, no sopro e no envase. O ajuste vale no
          painel na hora e fica no histórico com o seu nome.
        </p>
      </div>
      {erro && <p className="text-sm text-theme-danger">{erro}</p>}
      {!resposta && !erro && <p className="text-sm text-content-muted">Carregando as metas…</p>}
      {resposta && (
        <>
          <TabelaMetas resposta={resposta} />
          <FormMeta resposta={resposta} onSalvar={salvar} />
          <Historico itens={resposta.historico} nomes={new Map(resposta.maquinas.map((m) => [m.maquina, nome(m)]))} />
        </>
      )}
    </div>
  );
};

export default MetasMaquinas;
